export enum RenoTicketStatus {
  UNKNOWN = 'Unknown',
  // Kanbanfases voor renovatietickets (afgesproken met Leo).
  // Bewust eenvoudig; wordt later in de praktijk bijgestuurd.
  GEEN_REACTIE = 'Geen reactie',
  LOPENDE = 'Lopende',
  INFO_ONTVANGEN = 'Info ontvangen',
  AFGEROND = 'Afgerond',
}

export enum RenoTicketPriority {
  UNKNOWN = 'Unknown',
  LOW = 'Laag',
  MEDIUM = 'Gemiddeld',
  HIGH = 'Hoog',
}

export enum RenoTicketSource {
  UNKNOWN = 'Onbekend',
  INTERNAL = 'Intern',
  TENANT = 'Huurder',
  SUPPLIER = 'Leverancier',
}

/**
 * Type boodschap: waarover de communicatie gaat.
 *
 * De volgorde is die van de keuzelijst en volgt de loop van een dossier: eerst
 * informatie opvragen en langsgaan, dan prijzen en planning, dan uitvoering,
 * oplevering en facturatie.
 *
 * LET OP: moet gelijk blijven met `RenoMessageType` in
 * `yallyhub-be/src/reno-helpdesk/entities/reno-ticket-enums.ts` (Postgres-enum).
 */
export enum RenoMessageType {
  PLAATSBEZOEK = 'Plaatsbezoek',
  OPVRAGEN_INFO = 'Opvragen info',
  BESPREKING_PLANNEN = 'Bespreking plannen',
  OFFERTE = 'Offerte',
  ORGANISATIE_WERKEN = 'Organisatie der werken',
  INFO_HUURDERS = 'Info huurders',
  HERINNERING = 'Herinnering',
  OPLEVERING = 'Oplevering',
  FACTUUR = 'Factuur',
}

export interface RenoTicket {
  id: number;
  title: string;
  description?: string;
  status: RenoTicketStatus;
  priority: RenoTicketPriority;
  source: RenoTicketSource;
  messageType?: RenoMessageType | null;
  buildingId?: string | null;
  unitId?: string | null;
  renovationProjectId?: number | null;
  projectPartId?: number;
  supplierId?: number;
  reporterId?: number;
  assignedTo?: string;
  internalNotes?: string;
  dueDate?: string;
  /** Wanneer de interventie ter plaatse (gepland) doorgaat. */
  interventionDate?: string | null;
  tags?: string[];
  completedAt?: string;
  reopenedAt?: string;
  createdAt: string;
  updatedAt: string;
  building?: { id: string };
  unit?: { id: string; buildingId?: string };
  renovationProject?: {
    id: number;
    title: string;
    building?: { id: string; name?: string };
  };
  projectPart?: {
    id: number;
    title: string;
    label?: { id: number; name: string };
  };
  // @deprecated — gebruik supplierAssignments (meerdere leveranciers).
  supplier?: {
    id: number;
    name: string;
  };
  supplierAssignments?: {
    id: number;
    supplierId: number;
    sortOrder: number;
    supplier: { id: number; name: string; email?: string };
    /**
     * De gekozen contactpersoon bij deze leverancier. Staat die er, dan gaat een
     * mail over dit ticket naar díe persoon i.p.v. naar het algemene adres van de
     * firma. `null`/afwezig betekent "naar de firma".
     */
    contactId?: number | null;
    contact?: {
      id: number;
      firstName: string;
      lastName: string;
      email?: string | null;
      jobTitle?: string | null;
      phone_mobile?: string | null;
    } | null;
  }[];
  // Contactpersoon (was "Melder"): een Contact uit de contactenlijst.
  reporter?: {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
  };
  /**
   * E-mailtellers per communicatiekanaal, meegeleverd door de lijst-endpoints
   * (`GET /reno-helpdesk/tickets` en `.../grouped`) uit één aggregatiequery — de
   * lijst laadt de mails zelf niet mee. Zie `utils/renoEmailChannel.ts` voor de
   * kanaalregel; een mail die aan geen kanaal toegewezen kan worden, telt enkel
   * bij de leverancier, ook al is ze in beide weergaven zichtbaar.
   */
  emailCount?: number;
  supplierEmailCount?: number;
  tenantEmailCount?: number;
  unknownEmailCount?: number;
  /** Ongelezen ontvangen mails per kanaal — hierop staat de rode markering. */
  supplierUnreadEmailCount?: number;
  tenantUnreadEmailCount?: number;
  unreadEmailCount?: number;
  /**
   * Bedragen uit de **gekoppelde financiële documenten** (offertes/facturen onder
   * Renovatie → Financiële documenten), meegeleverd door de lijst- en
   * detailendpoints uit één aggregatiequery. Excl. btw, met creditnota's afgetrokken.
   * Ze staan nooit op het ticket zelf, zodat ze niet kunnen verouderen tegenover de
   * documenten.
   */
  approvedQuoteAmount?: number;
  invoiceAmount?: number;
  invoiceNumbers?: string[];
  financialDocumentCount?: number;
}

/**
 * Eén renovatieonderdeel binnen een project, met de tickets die eronder vallen.
 * Komt zo uit `GET /reno-helpdesk/tickets/grouped` — de tellers zijn backend-side
 * berekend en blijven dus correct, ook als niet alle tickets meegestuurd zijn.
 */
export interface RenoTicketPartGroup {
  /** null = tickets zonder onderdeel. */
  partId: number | null;
  partTitle: string;
  partLabel: string | null;
  partStatus: string | null;
  ticketCount: number;
  openCount: number;
  completedCount: number;
  tickets: RenoTicket[];
  truncated: boolean;
}

/** Eén renovatieproject met zijn onderdelen. */
export interface RenoTicketProjectGroup {
  /** null = tickets zonder renovatieproject. */
  projectId: number | null;
  projectTitle: string;
  projectStatus: string | null;
  buildingId: string | null;
  ticketCount: number;
  openCount: number;
  completedCount: number;
  parts: RenoTicketPartGroup[];
}

export interface RenoTicketGroupedResponse {
  groups: RenoTicketProjectGroup[];
  total: number;
  openCount: number;
  completionCount: number;
  loaded: number;
  ticketLimit: number;
  /** true wanneer de ticketlimiet bereikt is: verder filteren nodig. */
  truncated: boolean;
}

/**
 * Ticketrij voor de per-project weergave. De groepeersleutels zijn bewust
 * niet-numerieke strings: UniversalTable groepeert op veldwaarde en JS zou
 * numerieke sleutels herordenen, waardoor de backend-volgorde verloren gaat.
 */
export interface RenoGroupedTicketRow extends RenoTicket {
  projectGroupKey: string;
  partGroupKey: string;
  /**
   * Positie in de door de backend bepaalde volgorde (project → onderdeel →
   * ticketdatum). De tabel sorteert hier standaard op, zodat de groepen in
   * projectvolgorde blijven staan i.p.v. op ticketdatum.
   */
  groupOrder: number;
}

/** Groepeersleutel van een project (of "geen project"). */
export const renoProjectGroupKey = (projectId: number | null): string =>
  `project-${projectId ?? 'none'}`;

/** Groepeersleutel van een onderdeel binnen een project. */
export const renoPartGroupKey = (
  projectId: number | null,
  partId: number | null,
): string => `${renoProjectGroupKey(projectId)}-part-${partId ?? 'none'}`;

export const OPEN_STATUSES: RenoTicketStatus[] = [
  RenoTicketStatus.GEEN_REACTIE,
  RenoTicketStatus.LOPENDE,
  RenoTicketStatus.INFO_ONTVANGEN,
];

export const CLOSED_STATUSES: RenoTicketStatus[] = [
  RenoTicketStatus.AFGEROND,
];

export const STATUS_COLORS: Record<RenoTicketStatus, string> = {
  [RenoTicketStatus.UNKNOWN]: 'bg-gray-100 text-gray-600 border-gray-300',
  [RenoTicketStatus.GEEN_REACTIE]: 'bg-red-100 text-red-700 border-red-300',
  [RenoTicketStatus.LOPENDE]: 'bg-blue-100 text-blue-700 border-blue-300',
  [RenoTicketStatus.INFO_ONTVANGEN]: 'bg-teal-100 text-teal-700 border-teal-300',
  [RenoTicketStatus.AFGEROND]: 'bg-green-100 text-green-700 border-green-300',
};

// Code-prefix per communicatiekanaal: RS = leverancier, RT = huurder.
export function getRenoCommunicationCode(
  ticketId: number,
  channel: 'supplier' | 'tenant' = 'supplier',
): string {
  return `${channel === 'tenant' ? 'RT' : 'RS'}-${ticketId}`;
}
